import { useMemo } from 'react';

import { Messages } from 'config';
import { mapQuery } from 'utils/query';
import { Flex } from 'components/flex';
import { List } from 'components/list';
import { capitalize } from 'utils/string';
import { Loader } from 'components/loader';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { DeleteDialog } from 'components/delete-dialog';
import { formatPrice, joinStrings } from 'utils/helpers';
import { DownloadButton } from 'components/specified-buttons';
import { CashCardType, ListCardStyle, AccountingExpense } from 'types';
import {
  useExpensesQuery,
  useDeleteExpenseMutation,
} from 'store/api/accounting';
import {
  isMobile,
  handleMutation,
  useSearchQuery,
  useDownloadFile,
} from 'hooks';

import { ExpensesProps } from '../types';
import { CashCards } from '../cash-cards';

import * as Styles from './styles';
import { EditExpense, CreateExpense } from './form';

const template = '1fr 120px 1fr 1fr 1fr 1fr 104px';
const titles = [
  'Category',
  'Amount',
  'Payment Method',
  'Notes',
  'Employees',
  'Created By',
];

const getEventAmount = (
  currentExpense: AccountingExpense,
  eventId: number
): number =>
  Number(
    currentExpense?.events?.find((item) => item?.eventId === eventId)?.amount
  );

export const Expenses = ({ event }: ExpensesProps) => {
  const mobile = isMobile();
  const { debounceQuery, ...search } = useSearchQuery();

  const { data: accountingExpenses, ...accountingProps } = useExpensesQuery({
    search: debounceQuery,
    withReimbursement: true,
    eventIds: JSON.stringify([event.id]),
  });

  const { downloadFile } = useDownloadFile();

  const totalOut = useMemo(
    () =>
      accountingExpenses?.reduce(
        (acc, value) => acc + getEventAmount(value, event?.id),
        0
      ),
    [accountingExpenses]
  );

  const [remove, mutation] = useDeleteExpenseMutation();
  handleMutation({
    ...mutation,
    successMessage: `Expense ${Messages.DELETED_SUCCESSFULLY}`,
  });

  return (
    <Loader isFullScreen={false} isLoading={accountingProps.isLoading}>
      <Flex gap={24} fullHeight flexDirection={mobile ? 'column' : 'row'}>
        <CashCards
          showOnlyCash
          eventId={event.id}
          customTotalCash={totalOut}
          customType={CashCardType.Expenses}
        />

        <Flex gap={24} fullWidth fullHeight flexDirection="column">
          <List
            {...mapQuery(accountingProps)}
            {...search}
            count={accountingExpenses?.length}
            mainWrapperStyles={{ padding: 0 }}
            searchContainerStyle={{ margin: '16px 0' }}
            searchBarRightSlot={<CreateExpense eventId={event.id} />}
            header={
              <ListHeader padding="8px 0" titles={titles} template={template} />
            }
            searchInputProps={{
              placeholder:
                'Search category, payment method, amount, notes or created by',
            }}
            footer={
              <ListCard
                selected
                padding="16px"
                template={template}
                even={ListCardStyle.Green}
                values={[
                  <p className="semibold">Total</p>,
                  formatPrice(totalOut),
                ]}
              />
            }
          >
            {accountingExpenses?.map((value) => {
              const amount = formatPrice(getEventAmount(value, event?.id));
              const isReimbursement = String(value.id).startsWith(
                'reimbursement'
              );

              return (
                <ListCard
                  key={value.id}
                  padding="16px"
                  headers={titles}
                  template={template}
                  values={[
                    joinStrings(
                      value.categories.map((category) => category.name),
                      ','
                    ),
                    <Styles.Amount isNegative={+value.amount < 0}>
                      {amount}
                    </Styles.Amount>,
                    capitalize(value.paymentMethod),
                    value?.description,
                    joinStrings(
                      value.employees.map((employee) => employee?.user?.name),
                      ','
                    ),
                    value?.createdBy?.name,
                    <Flex gap={16} justifyContent="flex-end">
                      {value?.receiptUrl && (
                        <DownloadButton
                          url={value.receiptUrl}
                          onDownloadFile={downloadFile}
                        />
                      )}
                      {!isReimbursement && (
                        <>
                          <EditExpense data={value} />
                          <DeleteDialog
                            id={value.id}
                            action={remove}
                            title="Delete Expense"
                            name={`expense with ${amount}`}
                          />
                        </>
                      )}
                    </Flex>,
                  ]}
                />
              );
            })}
          </List>
        </Flex>
      </Flex>
    </Loader>
  );
};
